import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Member_Role } from '@gen/chat/v1/common_pb';
import { useStrings } from '@/i18n/Localization';
import {
  MEMBER_PAGE_SIZE,
  SLOW_MODE_CHOICES,
  TOPIC_PAGE_SIZE,
} from '@/protocol/limits';
import {
  canChangeRoles,
  canManageInvites,
  canManageMembers,
  canManageTopics,
  roleFromEnum,
  viewerFor,
  type RoleName,
} from '@/protocol/permissions';
import { useLiveQuery } from '@/store/hooks';
import {
  enqueueCreateInvite,
  enqueueDeleteTopic,
  enqueueLeaveConversation,
  enqueueResolveJoinRequest,
  enqueueSetBan,
  enqueueSetConversationPolicy,
  enqueueSetMemberRole,
  enqueueUpsertTopic,
  enqueueRevokeInvite,
  getConversation,
  getMember,
  listInvites,
  listMembers,
  listTopics,
} from '@/store/localStore';
import { useSession } from '@/transport/SessionProvider';
import type { ProfileResult } from '@/transport/workerCalls';
import type { Hex } from '@/store/ids';
import type {
  ConversationRecord,
  InviteRecord,
  MemberRecord,
  TopicRecord,
} from '@/store/types';
import styles from '@/components/panels.module.css';

/** Section 06 default invitation expiry is seven days; max uses default 100. */
const DEFAULT_INVITE_DAYS = 7;
const DEFAULT_INVITE_USES = 100;

function roleLabelKey(role: RoleName) {
  switch (role) {
    case 'OWNER':
      return 'group.roleOwner' as const;
    case 'ADMIN':
      return 'group.roleAdmin' as const;
    case 'MEMBER':
      return 'group.roleMember' as const;
    case 'RESTRICTED':
      return 'group.roleRestricted' as const;
    default:
      return 'group.roleNone' as const;
  }
}

/**
 * The Section 14 group info screen: members, own role, and the invite,
 * moderation and topic actions the viewer's permissions allow.
 *
 * Which controls appear is decided by `protocol/permissions.ts`, and that
 * decision is presentational only — Section 05 says an offline cache may hold
 * stale permissions and the server stays authoritative, so a control shown in
 * error produces a refused request rather than an unauthorized change.
 *
 * Ownership transfer is deliberately not offered. Section 06 requires recent
 * authentication for it, and this client has no re-authentication flow yet; an
 * action that always fails would be worse than one that says why it is absent,
 * which the note under the members list does.
 *
 * TODO(P3-04): offer ownership transfer once a recent-authentication ticket can
 * be minted, and pass it through `normalizeTransferOwnership`.
 */
export function GroupInfoScreen() {
  const { t, formatRelativeDay } = useStrings();
  const navigate = useNavigate();
  const { account, worker } = useSession();
  const params = useParams<{ conversationId: string }>();
  const conversationId = (params.conversationId ?? '') as Hex;

  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [joinRequests, setJoinRequests] = useState<ProfileResult[]>([]);

  const conversation = useLiveQuery<ConversationRecord | null>(
    () => getConversation(conversationId),
    [conversationId],
    null,
  );
  const members = useLiveQuery<MemberRecord[]>(
    () => listMembers(conversationId),
    [conversationId],
    [],
  );
  const topics = useLiveQuery<TopicRecord[]>(
    () => listTopics(conversationId),
    [conversationId],
    [],
  );
  const invites = useLiveQuery<InviteRecord[]>(
    () => listInvites(conversationId),
    [conversationId],
    [],
  );
  const ownMember = useLiveQuery<MemberRecord | null>(
    () => (account ? getMember(conversationId, account.id) : Promise.resolve(null)),
    [conversationId, account?.id],
    null,
  );

  const viewer = viewerFor(conversation, ownMember);

  // Members and topics are refreshed when the screen opens. The stored rows are
  // shown immediately so the screen is useful offline, and the query fills in
  // what changed while this device was away.
  useEffect(() => {
    if (!worker) {
      return;
    }
    void worker
      .call('list-members', { conversationId, cursor: null, limit: MEMBER_PAGE_SIZE })
      .catch(() => undefined);
    void worker
      .call('list-topics', { conversationId, cursor: null, limit: TOPIC_PAGE_SIZE })
      .catch(() => undefined);
  }, [worker, conversationId]);

  useEffect(() => {
    if (!worker || !canManageMembers(viewer)) {
      return;
    }
    void worker
      .call('list-join-requests', { conversationId, cursor: null, limit: MEMBER_PAGE_SIZE })
      .then((result) => {
        setJoinRequests(result.users);
      })
      .catch(() => undefined);
  }, [worker, conversationId, viewer]);

  const queued = useCallback(() => {
    worker?.notifyOutboxChanged();
  }, [worker]);

  const guard = useCallback(
    async (action: () => Promise<unknown>) => {
      setError(null);
      try {
        await action();
        queued();
      } catch {
        setError(t('status.localFailure'));
      }
    },
    [queued, t],
  );

  if (!account) {
    return null;
  }

  const memberCount = members.filter((member) => !member.banned).length;

  return (
    <main className={styles.screen} aria-label={t('group.info')}>
      <header className={styles.screenHeader}>
        <Link to={`/c/${conversationId}`} className={styles.back}>
          {t('nav.back')}
        </Link>
        <h1 className={styles.screenTitle} dir="auto">
          {conversation?.title || t('group.info')}
        </h1>
      </header>

      <section className={styles.section}>
        <p className={styles.rowMeta}>{t('group.memberCount', { count: memberCount })}</p>
        <p className={styles.rowMeta}>
          {t('group.yourRole', { role: t(roleLabelKey(viewer.role)) })}
        </p>
        {error === null ? null : (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </section>

      {/* --- Members ------------------------------------------------------ */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('group.members')}</h2>
        {members.length === 0 ? (
          <p className={styles.empty}>{t('group.memberCount', { count: 0 })}</p>
        ) : (
          <ul className={styles.list}>
            {members.map((member) => {
              const role = roleFromEnum(member.role);
              const isSelf = member.userId === account.id;
              return (
                <li key={member.userId} className={styles.row}>
                  <span className={styles.rowBody}>
                    <span className={styles.rowTitle} dir="auto">
                      {member.displayName || member.userId.slice(0, 8)}
                      {isSelf ? ` (${t('conversation.you')})` : ''}
                    </span>
                    <span className={styles.rowMeta}>
                      {t(roleLabelKey(role))}
                      {member.banned ? ` · ${t('group.banned')}` : ''}
                    </span>
                  </span>
                  <div className={styles.rowActions}>
                    {canChangeRoles(viewer) && !isSelf && role !== 'OWNER' ? (
                      <button
                        type="button"
                        className={styles.action}
                        onClick={() => {
                          void guard(() =>
                            enqueueSetMemberRole({
                              conversationId,
                              userId: member.userId,
                              originDeviceId: account.deviceId,
                              role: role === 'ADMIN' ? 'MEMBER' : 'ADMIN',
                              // A newly granted administrator receives no flags
                              // until the owner chooses them. Section 06 makes
                              // the flags explicit, so defaulting to "all"
                              // would grant more than was asked for.
                              permissions: 0,
                              expectedPolicyVersion: conversation?.policyVersion ?? 0,
                            }),
                          );
                        }}
                      >
                        {role === 'ADMIN' ? t('group.removeAdmin') : t('group.makeAdmin')}
                      </button>
                    ) : null}
                    {canManageMembers(viewer) && !isSelf && member.role !== Member_Role.OWNER ? (
                      <button
                        type="button"
                        className={`${styles.action} ${styles.destructive}`}
                        onClick={() => {
                          void guard(() =>
                            enqueueSetBan({
                              conversationId,
                              userId: member.userId,
                              originDeviceId: account.deviceId,
                              banned: !member.banned,
                            }),
                          );
                        }}
                      >
                        {member.banned ? t('group.unban') : t('group.ban')}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {viewer.role === 'OWNER' ? <p className={styles.hint}>{t('group.transferUnavailable')}</p> : null}
      </section>

      {/* --- Join requests ------------------------------------------------ */}
      {canManageMembers(viewer) ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('requests.title')}</h2>
          {joinRequests.length === 0 ? (
            <p className={styles.empty}>{t('requests.empty')}</p>
          ) : (
            <ul className={styles.list}>
              {joinRequests.map((profile) => (
                <li key={profile.userId} className={styles.row}>
                  <span className={styles.rowBody}>
                    <span className={styles.rowTitle} dir="auto">
                      {profile.displayName}
                    </span>
                    {profile.username === null ? null : (
                      <span className={styles.rowMeta}>@{profile.username}</span>
                    )}
                  </span>
                  <div className={styles.rowActions}>
                    {(['approve', 'reject'] as const).map((decision) => (
                      <button
                        key={decision}
                        type="button"
                        className={
                          decision === 'approve'
                            ? styles.action
                            : `${styles.action} ${styles.destructive}`
                        }
                        onClick={() => {
                          setJoinRequests((current) =>
                            current.filter((entry) => entry.userId !== profile.userId),
                          );
                          void guard(() =>
                            enqueueResolveJoinRequest({
                              conversationId,
                              requesterId: profile.userId,
                              originDeviceId: account.deviceId,
                              approve: decision === 'approve',
                            }),
                          );
                        }}
                      >
                        {decision === 'approve' ? t('requests.approve') : t('requests.reject')}
                      </button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {/* --- Invitations -------------------------------------------------- */}
      {canManageInvites(viewer) ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('invite.title')}</h2>
          {invites.length === 0 ? (
            <p className={styles.empty}>{t('invite.none')}</p>
          ) : (
            <ul className={styles.list}>
              {invites.map((invite) => (
                <li key={invite.token} className={styles.row}>
                  <span className={styles.rowBody}>
                    {/*
                      The token is shown so it can be shared. It is a secret:
                      Section 06 stores only its hash on the server, and this is
                      the one moment the plaintext exists on a client.
                    */}
                    <span className={styles.rowTitle}>{invite.token}</span>
                    <span className={styles.rowMeta}>
                      {t('invite.expires', { date: formatRelativeDay(invite.expiresAt) })}
                    </span>
                  </span>
                  <div className={styles.rowActions}>
                    <button
                      type="button"
                      className={styles.action}
                      onClick={() => {
                        void navigator.clipboard
                          .writeText(invite.token)
                          .then(() => {
                            setCopied(true);
                          })
                          .catch(() => {
                            setError(t('message.copyFailed'));
                          });
                      }}
                    >
                      {copied ? t('invite.copied') : t('invite.copy')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.action} ${styles.destructive}`}
                      onClick={() => {
                        void guard(async () => {
                          // The server stores invitations hashed, so revocation
                          // is addressed by the hash of the token rather than
                          // by the token itself.
                          const digest = await crypto.subtle.digest(
                            'SHA-256',
                            new TextEncoder().encode(invite.token),
                          );
                          const hash = [...new Uint8Array(digest)]
                            .map((byte) => byte.toString(16).padStart(2, '0'))
                            .join('');
                          return enqueueRevokeInvite({
                            conversationId,
                            originDeviceId: account.deviceId,
                            token: invite.token,
                            tokenHash: hash,
                          });
                        });
                      }}
                    >
                      {t('invite.revoke')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className={styles.buttonRow}>
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                void guard(() =>
                  enqueueCreateInvite({
                    conversationId,
                    originDeviceId: account.deviceId,
                    maxUses: DEFAULT_INVITE_USES,
                    expiresAt: Date.now() + DEFAULT_INVITE_DAYS * 86_400_000,
                    approval: false,
                  }),
                );
              }}
            >
              {t('invite.create')}
            </button>
          </div>
        </section>
      ) : null}

      {/* --- Topics -------------------------------------------------------- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('topic.title')}</h2>
        {topics.length === 0 ? (
          <p className={styles.empty}>{t('topic.empty')}</p>
        ) : (
          <ul className={styles.list}>
            {topics.map((topic) => (
              <li key={topic.topicId} className={styles.row}>
                <span className={styles.rowBody}>
                  <span className={styles.rowTitle} dir="auto">
                    {topic.title}
                  </span>
                  {topic.closed ? <span className={styles.rowMeta}>{t('topic.closed')}</span> : null}
                </span>
                {canManageTopics(viewer) ? (
                  <div className={styles.rowActions}>
                    <button
                      type="button"
                      className={styles.action}
                      onClick={() => {
                        void guard(() =>
                          enqueueUpsertTopic({
                            conversationId,
                            topicId: topic.topicId,
                            originDeviceId: account.deviceId,
                            title: topic.title,
                            closed: !topic.closed,
                            expectedVersion: topic.version,
                          }),
                        );
                      }}
                    >
                      {topic.closed ? t('topic.reopen') : t('topic.close')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.action} ${styles.destructive}`}
                      onClick={() => {
                        void guard(() =>
                          enqueueDeleteTopic({
                            conversationId,
                            topicId: topic.topicId,
                            originDeviceId: account.deviceId,
                            expectedVersion: topic.version,
                          }),
                        );
                      }}
                    >
                      {t('topic.delete')}
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {canManageTopics(viewer) ? (
          <div className={styles.field}>
            <label htmlFor="new-topic">{t('topic.name')}</label>
            <input
              id="new-topic"
              className={styles.input}
              dir="auto"
              value={newTopic}
              onChange={(event) => {
                setNewTopic(event.target.value);
              }}
            />
            <div className={styles.buttonRow}>
              <button
                type="button"
                className={styles.button}
                disabled={newTopic.trim().length === 0}
                onClick={() => {
                  const title = newTopic.trim();
                  setNewTopic('');
                  void guard(() =>
                    enqueueUpsertTopic({
                      conversationId,
                      topicId: null,
                      originDeviceId: account.deviceId,
                      title,
                      closed: false,
                      expectedVersion: 0,
                    }),
                  );
                }}
              >
                {t('topic.createAction')}
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {/* --- Policy -------------------------------------------------------- */}
      {canManageMembers(viewer) ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('slowmode.title')}</h2>
          <p className={styles.hint}>{t('slowmode.description')}</p>
          <div className={styles.field}>
            <label htmlFor="slow-mode">{t('slowmode.title')}</label>
            <select
              id="slow-mode"
              className={styles.select}
              value={String(conversation?.slowModeSeconds ?? 0)}
              onChange={(event) => {
                const seconds = Number(event.target.value);
                void guard(() =>
                  enqueueSetConversationPolicy({
                    conversationId,
                    originDeviceId: account.deviceId,
                    expectedVersion: conversation?.policyVersion ?? 0,
                    slowModeSeconds: seconds,
                    protectedContent: conversation?.protectedContent ?? false,
                    // History policy is fixed at creation and only an owner
                    // action changes it, so the current value is echoed rather
                    // than offered as an edit here.
                    historyPolicy: conversation?.historyPolicy === 2 ? 'SINCE_JOIN' : 'ALL_HISTORY',
                  }),
                );
              }}
            >
              {SLOW_MODE_CHOICES.map((seconds) => (
                <option key={seconds} value={String(seconds)}>
                  {seconds === 0 ? t('slowmode.off') : t('slowmode.seconds', { seconds })}
                </option>
              ))}
            </select>
          </div>

          <h2 className={styles.sectionTitle}>{t('protected.title')}</h2>
          <p className={styles.hint}>{t('protected.description')}</p>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={conversation?.protectedContent ?? false}
              onChange={(event) => {
                const next = event.target.checked;
                void guard(() =>
                  enqueueSetConversationPolicy({
                    conversationId,
                    originDeviceId: account.deviceId,
                    expectedVersion: conversation?.policyVersion ?? 0,
                    slowModeSeconds: conversation?.slowModeSeconds ?? 0,
                    protectedContent: next,
                    historyPolicy: conversation?.historyPolicy === 2 ? 'SINCE_JOIN' : 'ALL_HISTORY',
                  }),
                );
              }}
            />
            {t('protected.title')}
          </label>
        </section>
      ) : null}

      {/* --- Leaving ------------------------------------------------------- */}
      <section className={styles.section}>
        {viewer.role === 'OWNER' ? (
          // Section 06: "An owner must transfer ownership before leaving".
          <p className={styles.hint}>{t('group.ownerMustTransfer')}</p>
        ) : (
          <button
            type="button"
            className={`${styles.button}`}
            onClick={() => {
              void guard(async () => {
                await enqueueLeaveConversation({
                  conversationId,
                  originDeviceId: account.deviceId,
                });
                await navigate('/');
              });
            }}
          >
            {t('group.leave')}
          </button>
        )}
      </section>
    </main>
  );
}
